import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { ArrowLeft, CheckCircle2, UserX, Save, CreditCard } from "lucide-react";
import { toast } from "sonner";
import { Button } from "../../components/ui/button";
import { Textarea } from "../../components/ui/textarea";
import { Label } from "../../components/ui/label";
import { api, apiError } from "../../lib/api";
import { formatCurrency } from "../../lib/utils";

const TONE = {
  confirmed: "bg-teal-500/10 text-teal-300 border-teal-500/20",
  pending_payment: "bg-amber-500/10 text-amber-300 border-amber-500/20",
  completed: "bg-zinc-800 text-mb-fg2",
  cancelled: "bg-zinc-800 text-mb-fg2",
  no_show: "bg-red-500/10 text-red-300 border-red-500/20",
};

export default function AppointmentDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [a, setA] = useState(null);
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const load = () => {
    setError("");
    api.get(`/doctor/appointments/${id}`)
      .then((r) => { setA(r.data); setNotes(r.data.notes || ""); })
      .catch((e) => setError(apiError(e, "Failed to load")));
  };
  useEffect(load, [id]);

  const complete = async () => {
    setBusy(true);
    try {
      await api.post(`/doctor/appointments/${id}/complete`, { notes });
      toast.success("Appointment marked completed");
      load();
    } catch (e) { toast.error(apiError(e, "Failed")); } finally { setBusy(false); }
  };
  const noShow = async () => {
    setBusy(true);
    try {
      await api.post(`/doctor/appointments/${id}/no_show`, {});
      toast.success("Marked no-show");
      load();
    } catch (e) { toast.error(apiError(e, "Failed")); } finally { setBusy(false); }
  };
  const saveNotes = async () => {
    setBusy(true);
    try {
      // Backend exposes notes via /complete only when transitioning;
      // but to allow editing notes for completed appointments we re-call complete? No — only on confirmed.
      // For completed/no_show we just keep notes UI read-only.
      await api.post(`/doctor/appointments/${id}/complete`, { notes });
      toast.success("Notes saved");
      load();
    } catch (e) { toast.error(apiError(e, "Cannot save notes once finalized")); } finally { setBusy(false); }
  };

  if (error) return <div className="text-sm text-red-400" data-testid="appt-error">{error}</div>;
  if (!a) return <div className="text-sm text-mb-fg2">Loading…</div>;

  const canAct = ["confirmed", "pending_payment"].includes(a.status);

  return (
    <div className="space-y-6 max-w-4xl" data-testid="doc-appt-detail">
      <Link to="/doctor/appointments" className="text-sm text-mb-fg2 hover:text-white inline-flex items-center gap-2">
        <ArrowLeft className="h-4 w-4" /> Back
      </Link>
      <div className="rounded-xl border border-mb-border bg-mb-surface p-6 sm:p-8 grid sm:grid-cols-[1fr_auto] gap-4">
        <div>
          <div className="text-[10px] uppercase tracking-[0.3em] text-teal-400">◖ appointment</div>
          <h1 className="mt-2 text-2xl sm:text-3xl font-light tracking-tight" data-testid="appt-detail-name">
            {a.patient?.full_name || "—"}
          </h1>
          <div className="mt-1 text-sm text-mb-fg2">{a.patient?.email}</div>
          <div className="mt-4 flex flex-wrap gap-3 text-sm">
            <Tag k="Date & time" v={new Date(a.slot_start).toLocaleString()} />
            <Tag k="Fee" v={formatCurrency(a.consultation_fee)} />
          </div>
        </div>
        <span data-testid="appt-detail-status"
          className={`self-start text-[10px] uppercase tracking-[0.2em] px-2 py-1 rounded border ${TONE[a.status] || ""}`}>{a.status.replace("_", " ")}</span>
      </div>

      {/* Payment block (Phase 4 observability) */}
      <div className="rounded-xl border border-mb-border bg-mb-surface p-5" data-testid="appt-detail-payment">
        <div className="flex items-center gap-2 text-sm font-medium mb-3"><CreditCard className="h-4 w-4 text-teal-400" /> Payment</div>
        {a.payment ? (
          <div className="grid sm:grid-cols-3 gap-3 text-sm">
            <Field label="Status" value={a.payment.status} testid="payment-status" />
            <Field label="Amount" value={formatCurrency(a.payment.amount, { decimals: true })} testid="payment-amount" />
            <Field label="Provider" value={`${a.payment.provider} · ${(a.payment.provider_ref || "").slice(0, 8)}`} testid="payment-ref" />
          </div>
        ) : (
          <div className="text-xs text-mb-fg2">No payment record.</div>
        )}
      </div>

      {/* Notes + actions */}
      <div className="rounded-xl border border-mb-border bg-mb-surface p-5" data-testid="appt-detail-notes">
        <div className="space-y-2">
          <Label className="text-xs uppercase tracking-[0.2em] text-mb-fg2">Visit notes</Label>
          <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={6}
            disabled={!canAct && a.status !== "completed"}
            data-testid="appt-notes"
            className="bg-mb-surface2 border-mb-border text-white rounded-lg focus-visible:ring-teal-500" />
          {a.status === "completed" && (
            <div className="text-[10px] text-zinc-600 uppercase tracking-[0.2em]">Already completed — notes are read-only here. Re-save will fail.</div>
          )}
        </div>
        <div className="mt-5 flex flex-wrap gap-3 justify-end">
          {a.status === "completed" && a.notes !== notes && (
            <Button onClick={saveNotes} disabled={busy} variant="outline" data-testid="appt-save-notes"
              className="bg-transparent border-mb-border hover:bg-mb-surface2 text-white"><Save className="h-4 w-4 mr-2" /> Save notes</Button>
          )}
          {canAct && (
            <>
              <Button variant="outline" onClick={noShow} disabled={busy} data-testid="appt-no-show"
                className="bg-transparent border-red-500/40 hover:bg-red-500/10 text-red-300">
                <UserX className="h-4 w-4 mr-2" /> Mark no-show
              </Button>
              <Button onClick={complete} disabled={busy} data-testid="appt-complete"
                className="bg-teal-500 hover:bg-teal-400 text-zinc-950 font-medium">
                <CheckCircle2 className="h-4 w-4 mr-2" /> {busy ? "Saving…" : "Complete with notes"}
              </Button>
            </>
          )}
        </div>
      </div>

      <div className="text-xs text-mb-fg2">
        <button onClick={() => navigate(`/doctor/patients/${a.patient_id}`)} data-testid="appt-view-history"
          className="text-teal-400 hover:text-teal-300">→ View patient history with you</button>
      </div>
    </div>
  );
}

function Tag({ k, v }) {
  return (
    <div className="rounded-lg border border-mb-border bg-mb-surface2 px-3 py-1.5">
      <span className="text-[10px] uppercase tracking-[0.2em] text-mb-fg2 mr-2">{k}</span>
      <span className="text-zinc-200">{v}</span>
    </div>
  );
}
function Field({ label, value, testid }) {
  return (
    <div className="rounded-lg border border-mb-border bg-mb-surface2 px-3 py-2" data-testid={testid}>
      <div className="text-[10px] uppercase tracking-[0.2em] text-mb-fg2">{label}</div>
      <div className="mt-1 text-sm text-white">{value}</div>
    </div>
  );
}
